import React, { useState, memo, useCallback, useMemo, useEffect } from 'react';
import { useQuery, useApolloClient } from '@apollo/client';
import { GET_ASSIGNED_EMPLOYEES_SUMMARY, GET_EMPLOYEE_ASSIGNED_JOBS } from '../../graphql/queries';
import {
  AreaIndicator
} from '../../components/job-indicators';
import { formatJobDate, formatTime, getCheckoutTime, getBorderColor, isManualJob } from '../../utils/job-utils';
import JobBadges from '../schedule/indicators/JobBadges';
import NextCheckInDisplay from '../../components/NextCheckInDisplay';
import DueByDisplay from '../../components/DueByDisplay';
import Spinner from '../Spinner';
import { formatPropertyManagerNames, getPropertyManagers } from '../../utils/propertyManagers';

// Helper function to display user role properly (same as original)
const getDisplayRole = (employee) => {
  // Handle team assignments
  if (employee.role === 'TEAM') {
    return 'Team Assignment';
  }
  
  // First check if there's a department field, use that if available
  if (employee.department) {
    return employee.department;
  }
  
  // Normalize the role to handle case sensitivity and trim whitespace
  const normalizedRole = employee.role ? employee.role.toString().trim().toUpperCase() : '';
  
  // Otherwise, map the role to a user-friendly display name
  switch (normalizedRole) {
    case 'ADMIN':
      return 'Admin';
    case 'HOUSEKEEPER':
      return 'Housekeeper';
    case 'HOUSEKEEPER_MANAGER':
      return 'Housekeeping Manager';
    case 'INSPECTOR':
      return 'Inspector';
    case 'PROPERTY_MANAGER':
      return 'Property Manager';
    case 'MAINTENANCE':
      return 'Maintenance';
    case 'USER':
      return 'User';
    default:
      return employee.role || 'Staff';
  }
};

const AssignedTasksOptimized = memo(function AssignedTasksOptimized({ 
  queryVariables,
  totalJobsCount, 
  setEditingJob,
  useOptimizedLoading = false, // Feature flag to enable optimized loading
  clearCacheTrigger = 0 // Prop to trigger cache clearing from parent
}) {
  const client = useApolloClient();
  
  // Main component state - default to collapsed for performance
  const [isExpanded, setIsExpanded] = useState(true);
  
  // Employee-specific state for on-demand loading
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [employeeJobsCache, setEmployeeJobsCache] = useState({});
  const [loadingEmployees, setLoadingEmployees] = useState({});
  const [allEmployeesExpanded, setAllEmployeesExpanded] = useState(false); // Start collapsed

  // Helper function to get checkout date for display
  // Only shows date for CHECKIN-based jobs (like pre-arrival inspections)
  // because the previous checkout date might be different from the job's scheduled date
  const getCheckoutDate = (job) => {
    // Only show date for CHECKIN-based jobs
    if (job.serviceType?.timeBase !== 'CHECKIN') {
      return null;
    }
    
    // For CHECKIN-based jobs, use previous reservation checkout
    if (job.previousReservation?.check_out) {
      try {
        const checkoutDate = new Date(job.previousReservation.check_out);
        const month = checkoutDate.getMonth() + 1;
        const day = checkoutDate.getDate();
        return `${month}/${day}`;
      } catch (error) {
        return null;
      }
    }
    
    return null;
  };

  // 🎯 NEW: Memoize query variables to prevent unnecessary re-renders
  const memoizedQueryVariables = useMemo(() => queryVariables, [
    queryVariables.input.startDate,
    queryVariables.input.endDate,
    queryVariables.input.areas?.join(','),
    queryVariables.input.serviceTypes?.join(','),
    queryVariables.input.propertyIds?.join(',')
  ]);

  // Load employee summary (lightweight query)
  const { 
    data: employeeSummaryData, 
    loading: summaryLoading, 
    error: summaryError,
    refetch: refetchSummary
  } = useQuery(GET_ASSIGNED_EMPLOYEES_SUMMARY, {
    variables: memoizedQueryVariables,
    skip: !useOptimizedLoading,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: false,
    onError: (error) => {
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        // Error handling without logging
      }
    }
  });

  // Fallback to original data structure for non-optimized mode
  const employees = useMemo(() => {
    if (useOptimizedLoading && employeeSummaryData?.assignedEmployeesSummary) {
      return employeeSummaryData.assignedEmployeesSummary.employees;
    }
    return [];
  }, [useOptimizedLoading, employeeSummaryData]);

  // Load jobs for specific employee on-demand
  // Omit serviceTypes filter so cross-department jobs are included;
  // the employee summary query already ensures only relevant employees appear.
  const loadEmployeeJobs = useCallback(async (employeeId) => {
    if (employeeJobsCache[employeeId] || loadingEmployees[employeeId]) return;
    
    setLoadingEmployees(prev => ({ ...prev, [employeeId]: true }));
    
    try {
      const { serviceTypes, ...inputWithoutServiceTypes } = memoizedQueryVariables.input;
      const result = await client.query({
        query: GET_EMPLOYEE_ASSIGNED_JOBS,
        variables: {
          input: {
            ...inputWithoutServiceTypes,
            employeeId: parseInt(employeeId),
            limit: 100
          }
        },
        fetchPolicy: 'cache-first',
        errorPolicy: 'all'
      });
      
      if (result.data?.employeeAssignedJobs?.items) {
        setEmployeeJobsCache(prev => ({
          ...prev,
          [employeeId]: result.data.employeeAssignedJobs.items
        }));
      }
    } catch (error) {
      if (error.name !== 'AbortError' && !error.message.includes('cancelled')) {
        // Error handling without logging
      }
    } finally {
      setLoadingEmployees(prev => ({ ...prev, [employeeId]: false }));
    }
  }, [memoizedQueryVariables, client, employeeJobsCache, loadingEmployees]);

  const toggleAccordion = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Toggle individual employee accordion with on-demand loading
  const toggleEmployeeAccordion = useCallback((e, employeeId) => {
    e.stopPropagation();
    
    const wasExpanded = expandedEmployees[employeeId];
    
    setExpandedEmployees(prev => ({
      ...prev,
      [employeeId]: !prev[employeeId]
    }));

    // Load jobs if expanding and we haven't loaded them yet
    if (!wasExpanded && useOptimizedLoading) {
      loadEmployeeJobs(employeeId);
    }
  }, [expandedEmployees, useOptimizedLoading, loadEmployeeJobs]);

  // Toggle all employee tabs at once
  const toggleAllEmployeeTabs = useCallback((e) => {
    e.stopPropagation();
    const newState = !allEmployeesExpanded;
    setAllEmployeesExpanded(newState);
    
    // Update all employee expanded states
    const employeeIds = employees.map(employee => employee.id);
    const newExpandedState = {};
    employeeIds.forEach(id => {
      newExpandedState[id] = newState;
    });
    setExpandedEmployees(newExpandedState);

    // If expanding all and using optimized loading, load jobs for all employees
    if (newState && useOptimizedLoading) {
      employeeIds.forEach(employeeId => {
        if (!employeeJobsCache[employeeId] && !loadingEmployees[employeeId]) {
          loadEmployeeJobs(employeeId);
        }
      });
    }
  }, [allEmployeesExpanded, employees, useOptimizedLoading, employeeJobsCache, loadingEmployees, loadEmployeeJobs]);

  // 🎯 CRITICAL: Clear employee jobs cache when query variables change (date filter changes)
  useEffect(() => {
    if (useOptimizedLoading) {
      // Clear cache and reset states
      setEmployeeJobsCache({});
      setExpandedEmployees({});
      setAllEmployeesExpanded(false);
      setLoadingEmployees({});
      
      // 🎯 CRITICAL FIX: Clear ALL Apollo Client caches for assignment-related queries
      try {
        // Clear employee-specific queries with specific arguments to be more thorough
        client.cache.evict({ 
          id: 'Query',
          fieldName: 'employeeAssignedJobs'
        });
        
        // Clear summary queries with specific arguments
        client.cache.evict({ 
          id: 'Query',
          fieldName: 'assignedEmployeesSummary'
        });
        
        // Clear main assignment queries
        client.cache.evict({ 
          id: 'Query',
          fieldName: 'jobsForAssignments'
        });
        client.cache.evict({ 
          id: 'Query',
          fieldName: 'assignedJobsForAssignments'
        });
        
        // Clear any cached eligible users data
        client.cache.evict({ 
          id: 'Query',
          fieldName: 'eligibleUsersForJobs'
        });
        
        // 🎯 NEW: Also clear any cached job data that might be stale
        client.cache.evict({ 
          id: 'Query',
          fieldName: 'jobs'
        });
        
        // Run garbage collection to clean up orphaned references
        client.cache.gc();
      } catch (error) {
        // Error clearing caches - fail silently
      }
      
      // 🎯 NEW: Force refetch of summary data to ensure fresh data
      if (refetchSummary) {
        setTimeout(() => {
          refetchSummary();
        }, 100); // Small delay to ensure cache clearing is complete
      }
    }
  }, [memoizedQueryVariables.input.startDate, memoizedQueryVariables.input.endDate, useOptimizedLoading, client, refetchSummary]);

  // 🎯 NEW: Clear cache when parent triggers cache clearing (for job assignments/unassignments)
  useEffect(() => {
    if (useOptimizedLoading && clearCacheTrigger > 0) {
      // Clear local cache and reset states
      setEmployeeJobsCache({});
      setExpandedEmployees({});
      setLoadingEmployees({});
      
      // Also trigger summary refetch
      if (refetchSummary) {
        refetchSummary();
      }
    }
  }, [clearCacheTrigger, useOptimizedLoading, refetchSummary]);

  // 🎯 CRITICAL: Also clear cache when employee summary data changes (additional safety)
  useEffect(() => {
    if (useOptimizedLoading && employeeSummaryData?.assignedEmployeesSummary) {
      // Get current employee IDs from new data
      const currentEmployeeIds = employeeSummaryData.assignedEmployeesSummary.employees.map(emp => emp.id);
      
      // Remove cached data for employees no longer in the summary
      setEmployeeJobsCache(prev => {
        const newCache = {};
        Object.keys(prev).forEach(cachedId => {
          if (currentEmployeeIds.includes(parseInt(cachedId))) {
            newCache[cachedId] = prev[cachedId];
          }
        });
        return newCache;
      });
      
      // 🎯 CRITICAL FIX: Also clear Apollo cache for employees no longer in summary
      try {
        // Clear all employee-specific caches to ensure fresh data
        client.cache.evict({ fieldName: 'employeeAssignedJobs' });
        client.cache.gc();
      } catch (error) {
        // Error clearing employee caches - fail silently
      }
    }
  }, [employeeSummaryData?.assignedEmployeesSummary?.employees, useOptimizedLoading, client]);

  // Get jobs for an employee (from cache or return empty array)
  const getEmployeeJobs = useCallback((employeeId) => {
    if (useOptimizedLoading) {
      const jobs = employeeJobsCache[employeeId] || [];
      return jobs;
    }
    // Fallback to original groupedAssignedJobs structure would go here
    return [];
  }, [useOptimizedLoading, employeeJobsCache]);

  if (summaryLoading && useOptimizedLoading) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-sm text-gray-600">Loading assigned tasks...</p>
        </div>
      </div>
    );
  }

  if (summaryError && useOptimizedLoading) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-6 text-center text-red-500">
          <p>Error loading assigned tasks</p>
          <button 
            onClick={() => refetchSummary()}
            className="mt-2 px-4 py-2 bg-red-100 text-red-800 rounded hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      {/* Header */}
      <div 
        className="p-4 bg-blue-50 border-b flex items-center justify-between cursor-pointer hover:bg-blue-100"
        onClick={toggleAccordion}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <h2 className="text-lg font-semibold whitespace-nowrap">Assigned Tasks</h2>
          <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full whitespace-nowrap">
             {useOptimizedLoading 
               ? `${employeeSummaryData?.assignedEmployeesSummary?.totalJobs || 0} jobs, ${employees.length} employees`
               : `${totalJobsCount.assigned || 0} assigned`
             }
           </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {employees.length > 0 && (
            <button
              onClick={toggleAllEmployeeTabs}
              className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 rounded border border-blue-200 hover:bg-blue-50 whitespace-nowrap"
            >
              {allEmployeesExpanded ? 'Collapse All' : 'Expand All'}
            </button>
          )}
          <div className="text-gray-500">
            {isExpanded ? '▼' : '▶'}
          </div>
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="divide-y divide-gray-200">
          {employees.map((employee) => {
            const isEmployeeExpanded = expandedEmployees[employee.id] !== undefined 
              ? expandedEmployees[employee.id] 
              : allEmployeesExpanded;
            
            const jobs = getEmployeeJobs(employee.id);
            const isLoading = loadingEmployees[employee.id];
            
            return (
              <div key={`employee-${employee.id}-${employee.name}`} className={`${employee.isTeamAssignment ? 'bg-blue-50 border-2 border-blue-200' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
                <div 
                  className={`p-4 ${employee.isTeamAssignment ? 'bg-blue-100' : 'bg-gray-50'} border-b flex items-center justify-between cursor-pointer hover:${employee.isTeamAssignment ? 'bg-blue-200' : 'bg-gray-100'}`}
                  onClick={(e) => toggleEmployeeAccordion(e, employee.id)}
                >
                  <div className="flex items-center gap-2">
                    {employee.isTeamAssignment && (
                      <svg className="h-5 w-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    )}
                    <div className="flex flex-col">
                      <h3 className="font-medium">{employee.name}</h3>
                      {employee.isTeamAssignment && employee.memberCount > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-blue-600 font-medium">
                            {employee.memberCount} member{employee.memberCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      employee.isTeamAssignment 
                        ? 'bg-blue-200 text-blue-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {getDisplayRole(employee)}
                    </span>
                    <span className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full">
                      {employee.jobCount} {employee.jobCount === 1 ? 'job' : 'jobs'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {isLoading && <Spinner size="sm" />}
                    <div className="text-gray-500">
                      {isEmployeeExpanded ? '▼' : '▶'}
                    </div>
                  </div>
                </div>

                {/* Employee Jobs */}
                {isEmployeeExpanded && (
                  <div className="p-2 space-y-2">
                    {isLoading ? (
                      <div className="p-4 text-center">
                        <Spinner size="sm" />
                        <p className="mt-2 text-sm text-gray-600">Loading jobs...</p>
                      </div>
                    ) : jobs.length > 0 ? (
                      jobs.map((job, index) => {
                        const isFlipJob = job.isFlip;
                        const serviceTypeColor = job.serviceType?.color || '#6B7280';
                        
                        // For flip jobs, use red border; otherwise use service type color
                        const serviceTypeColorStyle = {
                          borderLeftColor: isFlipJob ? '#ef4444' : serviceTypeColor
                        };
                        
                        const finalBorderClass = isFlipJob 
                          ? 'border-l-[24px] border-2 border-red-500'
                          : 'border-l-[24px] border border-gray-200';
                        
                        return (
                          <div 
                            key={`job-${job.id}-${employee.id}`} 
                            className={`bg-gray-50 rounded-lg shadow ${finalBorderClass} cursor-pointer relative mx-2`}
                            style={serviceTypeColorStyle}
                          >
                            <div className="flex">
                              <div className="flex-1 p-2 lg:p-4">
                                <div className="grid grid-cols-[3fr_2fr] gap-4">
                                  {/* Left Column - 3/5 width */}
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <h4 className="font-medium">{job.property.name}</h4>

                                    </div>
                                    <p className="text-xs text-gray-500 mb-1">
                                      {formatJobDate(job.scheduledDate)}
                                      {formatPropertyManagerNames(getPropertyManagers(job.property)) && (
                                        <span className="text-gray-400">
                                          {' • PM: '}
                                          <span className="text-gray-600">{formatPropertyManagerNames(getPropertyManagers(job.property))}</span>
                                        </span>
                                      )}
                                    </p>
                                    {/* Show Guest Checkout for non-custom jobs */}
                                    {!isManualJob(job) && job.serviceType?.timeBase !== 'CUSTOM' && (
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className="flex items-center">
                                          <div className="text-sm mr-1 font-medium text-gray-600">
                                            Guest Checkout:
                                          </div>
                                          {job.hasTimeOverride ? (
                                            <div className="flex flex-col text-sm">
                                              {getCheckoutTime(job) && (
                                                <span className="line-through text-gray-400">
                                                  {formatTime(getCheckoutTime(job))}
                                                  {getCheckoutDate(job) && <span className="ml-1">on {getCheckoutDate(job)}</span>}
                                                </span>
                                              )}
                                              <span className="flex items-center text-sky-600 font-semibold">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-0.5" viewBox="0 0 20 20" fill="currentColor">
                                                  <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                                </svg>
                                                {formatTime(job.startTime)}
                                                {getCheckoutDate(job) && <span className="ml-1">on {getCheckoutDate(job)}</span>}
                                              </span>
                                            </div>
                                          ) : (
                                            <div className="text-sm text-gray-800 font-semibold">
                                              {formatTime(getCheckoutTime(job))}
                                              {getCheckoutDate(job) && <span className="text-gray-600 ml-1">on {getCheckoutDate(job)}</span>}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* Show Start Time for custom/manual jobs */}
                                    {(isManualJob(job) || job.serviceType?.timeBase === 'CUSTOM') && job.startTime && (
                                      <div className="flex items-center gap-2 mb-1">
                                        <div className="flex items-center">
                                          <div className="text-sm mr-1 font-medium text-gray-600">
                                            Start Time:
                                          </div>
                                          <div className="text-sm text-gray-800 font-semibold">{formatTime(job.startTime)}</div>
                                        </div>
                                      </div>
                                    )}
                                    <div className="text-sm mt-1 bg-blue-50 p-1 rounded">
                                      <span className="inline-flex items-center">
                                        <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <span className="text-gray-600 font-medium mr-1">Due by:</span>
                                        <span className="text-blue-700 font-semibold"><DueByDisplay job={job} /></span>
                                      </span>
                                    </div>
                                    {job.property.area && (
                                      <div className="mt-2">
                                        <AreaIndicator area={job.property.area} />
                                      </div>
                                    )}
                                  </div>

                                  {/* Right Column - 2/5 width */}
                                  <div className="flex flex-col justify-between">
                                    {/* Badges at top */}
                                    <div className="flex flex-wrap flex-row-reverse gap-1 justify-start mb-2">
                                      {/* Use JobBadges component to ensure early check-in detection works for cleaning jobs */}
                                      <JobBadges job={job} allJobs={jobs} />
                                    </div>
                                    
                                    {/* Edit button at bottom */}
                                    <div className="flex justify-end">
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation(); // Prevent triggering accordion toggle
                                          setEditingJob(job);
                                        }}
                                        className="text-gray-400 hover:text-gray-600"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          className="h-5 w-5"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                          />
                                        </svg>
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        <p>No jobs loaded yet</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          
          {employees.length === 0 && (
            <div className="p-6 text-center text-gray-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <p>No assigned tasks found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default AssignedTasksOptimized; 